import React from 'react'
import { listAllTestimonials, deleteTestimonial, type Testimonial } from '../services/testimonialsService'
import TestimonialFormModal from '../components/TestimonialFormModal'
import { Table, Tr, Th, Td } from '@/shared/ui/Table'
import { Button, Input, Badge } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreVertical, Search } from 'lucide-react'

export default function TestimonialsAdminPage() {
  const [items, setItems] = React.useState<Testimonial[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [openForm, setOpenForm] = React.useState(false)
  const [current, setCurrent] = React.useState<Testimonial | null>(null)
  const [q, setQ] = React.useState('')

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listAllTestimonials()
      setItems(list)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar depoimentos')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { load() }, [load])

  function onCreate() { setCurrent(null); setOpenForm(true) }
  function onEdit(t: Testimonial) { setCurrent(t); setOpenForm(true) }
  async function onDelete(t: Testimonial) {
    if (!confirm('Excluir depoimento?')) return
    try { await deleteTestimonial(t.id); await load() } catch (e: any) { setError(e?.message ?? 'Falha ao excluir') }
  }

  const filtered = items.filter(it => (
    !q || it.author_name?.toLowerCase().includes(q.toLowerCase()) || it.text?.toLowerCase().includes(q.toLowerCase())
  ))

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <TestimonialFormModal open={openForm} onClose={()=> setOpenForm(false)} onSaved={load} initial={current ?? undefined} />
      <div className="mb-6">
        <div className="text-2xl font-semibold">Depoimentos</div>
        <div className="text-sm text-foreground/70">Gerencie os depoimentos exibidos na Home.</div>
      </div>
      {error && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input value={q} onChange={(e)=> setQ(e.target.value)} placeholder="Buscar depoimentos" className="pl-9" />
        </div>
        <Button onClick={onCreate}>Novo depoimento</Button>
      </div>
      <Table>
        <thead>
          <Tr>
            <Th>Autor</Th>
            <Th>Texto</Th>
            <Th>Visível</Th>
            <Th className="w-12 text-right">Ações</Th>
          </Tr>
        </thead>
        <tbody>
          {filtered.map((t) => (
            <Tr key={t.id}>
              <Td className="max-w-[260px]">
                <div className="flex items-center gap-2">
                  {t.avatar_url && <img src={t.avatar_url} alt="avatar" className="h-8 w-8 rounded-full object-cover" />}
                  <div>
                    <div className="text-sm font-medium">{t.author_name}</div>
                    {t.author_handle && <div className="text-xs text-foreground/70">{t.author_handle}</div>}
                  </div>
                </div>
              </Td>
              <Td className="text-sm text-foreground/80 truncate max-w-[540px]">{t.text}</Td>
              <Td>{t.visible ? <Badge variant="soft" color="success">Sim</Badge> : <Badge variant="soft" color="error">Não</Badge>}</Td>
              <Td className="text-right">
                <DropdownMenu
                  align="end"
                  trigger={<Button variant="outline" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button>}
                  items={[
                    { label: 'Editar', onSelect: () => onEdit(t) },
                    { label: <span className="text-destructive">Excluir</span>, onSelect: () => onDelete(t) },
                  ]}
                />
              </Td>
            </Tr>
          ))}
          {filtered.length === 0 && (
            <Tr>
              <Td colSpan={4} className="py-10 text-center text-sm text-foreground/70">Nenhum depoimento encontrado.</Td>
            </Tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}

